<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import { ApiClientError } from "@shopware/api-client";
import type { ApiError } from "@shopware/api-client";
import { defu } from "defu";
import { useVuelidate } from "@vuelidate/core";
import { customValidators } from "@/i18n/utils/i18n-validators";

const { required, minLength, email } = customValidators();

const props = defineProps<{
  quantity: number;
}>();

const emits = defineEmits<{
  (e: "close"): void;
}>();

const { quantity } = toRefs(props);

const { selectedProducts, clear } = useCustomProductConfigurator(quantity);

const quotationPopup = ref(null);

onClickOutside(quotationPopup, () => emits("close"));

type Translations = {
  form: {
    salutation: string;
    salutationPlaceholder: string;
    firstName: string;
    firstNamePlaceholder: string;
    lastName: string;
    lastNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    phone: string;
    phonePlaceholder: string;
    subject: string;
    subjectPlaceholder: string;
    comment: string;
    commentPlaceholder: string;
    privacy: string;
    dataProtection: string;
    submit: string;
    messages: {
      contactFormSuccess: string;
    };
  };
};

let translations: Translations = {
  form: {
    salutation: "Salutation",
    salutationPlaceholder: "Enter salutation...",
    firstName: "First name",
    firstNamePlaceholder: "Enter first name...",
    lastName: "Last name",
    lastNamePlaceholder: "Enter last name...",
    email: "Email address",
    emailPlaceholder: "Enter email address...",
    phone: "Phone number",
    phonePlaceholder: "Enter phone number...",
    subject: "Subject",
    subjectPlaceholder: "Enter subject...",
    comment: "Comment",
    commentPlaceholder: "Enter comment...",
    privacy: "Privacy",
    dataProtection: "I have read the data protection information.",
    submit: "Submit",
    messages: {
      contactFormSuccess:
        "We have received your contact request and will process it as soon as possible.",
    },
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const loading = ref<boolean>();
const formSent = ref<boolean>(false);

type ErrorMessages = ApiClientError<{
  errors: ApiError[];
}>;

const errorMessages = ref<ErrorMessages[]>([]);

const { getSalutations } = useSalutations();
const { foreignKey } = useNavigationContext();
const { apiClient } = useShopwareContext();

const cartData = computed(() => {
  return selectedProducts.value
    .map((product) =>
      [
        product.name,
        `Artikl.-Nr: ${product.productNumber}`,
        `Menge: ${product.quantity}`,
      ].join(" - ")
    )
    .join("\n");
});

const subjectData = computed(() =>
  selectedProducts.value.length > 0
    ? [
        "Angebotsanfrage für",
        `Artikl.-Nr: ${selectedProducts.value[0].productNumber}`,
        selectedProducts.value[0].name,
      ].join(" - ")
    : ""
);

const state = reactive({
  companyName: "",
  salutationId: "",
  firstName: "",
  lastName: "",
  email: "",
  subject: "",
  comment: "",
  phone: "",
  checkbox: false,
  support: false,
});

watch(
  () => subjectData.value,
  (newSubject) => {
    state.subject = newSubject;
  }
);

const rules = computed(() => ({
  email: {
    required,
    email,
  },
  companyName: {
    required,
    minLength: minLength(3),
  },
  firstName: {
    required,
    minLength: minLength(3),
  },
  lastName: {
    required,
    minLength: minLength(3),
  },
  salutationId: {
    required,
  },
  phone: {
    required,
    minLength: minLength(3),
  },
  subject: {
    required,
    minLength: minLength(3),
  },
  checkbox: {
    required,
    isTrue: (value: boolean) => value === true,
  },
}));

const { t } = useI18n();

const $v = useVuelidate(rules, state);
const invokeSubmit = async () => {
  $v.value.$touch();
  const valid = await $v.value.$validate();
  if (valid) {
    loading.value = true;
    state.lastName += "\n" + t("form.company") + ": " + state.companyName;
    if (state.support) {
      state.comment += "\n\n" + t("product.quotation.support") + "\n";
    }
    state.comment += "\n" + t("listing.products") + ":\n\n";
    state.comment += cartData.value;
    try {
      await apiClient.invoke("sendContactMail post /contact-form", {
        body: {
          ...state,
          navigationId: foreignKey.value,
        },
      });
      formSent.value = true;
    } catch (e) {
      if (e instanceof ApiClientError) {
        errorMessages.value = e.details.errors;
      }
    } finally {
      loading.value = false;
      clear();
    }
  }
};
</script>
<template>
  <div
    id="QuotationModal"
    class="[&:not(.show)]:opacity-0 w-full h-full fixed top-0 left-0 z-[100] overflow-y-auto show"
  >
    <div
      class="w-full h-full fixed top-0 left-0 bg-skin-primary opacity-40 -z-[1]"
    ></div>
    <div class="popup-dialog flex items-center justify-center my-10">
      <div
        ref="quotationPopup"
        class="popup-content w-full md:w-[75.625rem] py-10 px-5 md:p-10 bg-skin-white rounded relative"
      >
        <div class="popup-header mb-5 flex items-center">
          <h3 class="font-bold text-2xl">
            {{ $t("product.quotation.label") }}
          </h3>
          <button
            type="button"
            class="popup-close w-6 h-6 ml-auto bg-no-repeat bg-[length:100%] bg-humburger-close"
            @click="$emit('close')"
          ></button>
        </div>
        <div class="popup-body py-5">
          <form
            class="flex flex-col max-w-full mx-auto relative test"
            @submit.prevent="invokeSubmit"
          >
            <div
              v-if="loading"
              class="absolute inset-0 flex items-center justify-center z-10 bg-white/50"
            >
              <div
                class="h-15 w-15 i-carbon-progress-bar-round animate-spin c-gray-500"
              />
            </div>
            <template v-if="!formSent">
              <div
                v-for="product in selectedProducts"
                :key="product.id"
                class="relative mb-5 [&>.group:not(:last-child)]:border-b overflow-hidden"
              >
                <div class="group relative flex py-5 border-skin-primary">
                  <div class="flex items-center flex-1">
                    <div class="w-[3.75rem] h-[3.75rem]">
                      <NuxtImg
                        format="webp"
                        loading="lazy"
                        :src="product?.cover?.url"
                        :alt="product?.name"
                        :width="product?.cover?.metaData?.width"
                        :height="product?.cover?.metaData?.height"
                        class="object-contain h-full w-full"
                      />
                    </div>
                    <div
                      class="flex flex-wrap flex-1 pl-2.5 mobile:gap-y-1 md:flex-row flex-col"
                    >
                      <div
                        class="md:w-3/5 md:px-5 flex flex-col justify-center md:border-r border-skin-primary md:py-4"
                      >
                        <h5 class="font-bold text-md mb-2">
                          {{ product?.name }}
                        </h5>
                      </div>
                      <div
                        class="md:w-3/12 md:px-5 flex md:justify-center items-center md:border-r border-skin-primary md:py-4"
                      >
                        <p class="mobile:mb-0">
                          {{ $t("product.productNumber") }}:
                          {{ product?.productNumber }}
                        </p>
                      </div>
                      <div
                        class="md:w-[15%] md:px-5 flex flex-col md:flex-row items-center md:justify-center md:py-4"
                      >
                        <p class="w-full">
                          {{ $t("account.order.quantity") }}: {{ quantity }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="grid grid-cols-12 gap-5">
                <div class="col-span-12">
                  <input
                    id="first-name"
                    v-model="state.companyName"
                    name="first-name"
                    type="text"
                    :placeholder="$t('form.company') + ' *'"
                    autocomplete="first-name"
                    class="appearance-none relative block w-full text-[14px] h-11 pt-[10px] py-2 px-5 border placeholder-skin-secondary text-gray-900 rounded-[3px] focus:outline-none focus:ring-gray-300 focus:z-10 sm:text-sm"
                    :class="[
                      $v.companyName.$error
                        ? 'border-red-600 focus:border-red-600'
                        : 'border-skin-primary focus:border-skin-primary',
                    ]"
                    @blur="$v.companyName.$touch()"
                  />
                  <span
                    v-if="$v.companyName.$error"
                    class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
                  >
                    {{ $v.companyName.$errors[0].$message }}
                  </span>
                </div>
                <div class="col-span-12">
                  <!--<label for="salutation">{{ $t('form.salutation') }} *</label>-->
                  <select
                    id="salutation"
                    v-model="state.salutationId"
                    name="salutation"
                    class="input-sty bg-no-repeat text-[14px] pt-[10px] h-11 bg-select-arrow w-full rounded-[3px] py-2 px-5 focus:ring-gray-300"
                    :class="[
                      $v.salutationId.$error
                        ? 'border-red-600 focus:border-red-600'
                        : 'border-[#D9D9D9] focus:border-skin-primary',
                    ]"
                    @blur="$v.salutationId.$touch()"
                  >
                    <option value="" disabled selected>
                      {{ $t("form.salutation") }} *
                    </option>
                    <option
                      v-for="salutation in getSalutations"
                      :key="salutation.id"
                      :value="salutation.id"
                    >
                      {{ salutation.displayName }}
                    </option>
                  </select>
                  <span
                    v-if="$v.salutationId.$error"
                    class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
                  >
                    {{ $v.salutationId.$errors[0].$message }}
                  </span>
                </div>
                <div class="col-span-12 md:col-span-6">
                  <!--<label for="first-name">{{ $t('form.firstName') }} *</label>-->
                  <input
                    id="first-name"
                    v-model="state.firstName"
                    name="first-name"
                    type="text"
                    :placeholder="$t('form.firstName') + ' *'"
                    autocomplete="first-name"
                    class="appearance-none relative block w-full text-[14px] h-11 pt-[10px] py-2 px-5 border placeholder-skin-secondary text-gray-900 rounded-[3px] focus:outline-none focus:ring-gray-300 focus:z-10 sm:text-sm"
                    :class="[
                      $v.firstName.$error
                        ? 'border-red-600 focus:border-red-600'
                        : 'border-skin-primary focus:border-skin-primary',
                    ]"
                    @blur="$v.firstName.$touch()"
                  />
                  <span
                    v-if="$v.firstName.$error"
                    class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
                  >
                    {{ $v.firstName.$errors[0].$message }}
                  </span>
                </div>
                <div class="col-span-12 md:col-span-6">
                  <!--<label for="last-name">{{ $t('form.lastName') }} *</label>-->
                  <input
                    id="last-name"
                    v-model="state.lastName"
                    name="last-name"
                    type="text"
                    :placeholder="$t('form.lastName') + ' *'"
                    autocomplete="last-name"
                    class="appearance-none relative block w-full text-[14px] h-11 py-2 px-5 pt-[10px] border placeholder-skin-secondary text-gray-900 rounded-[3px] focus:outline-none focus:ring-gray-300 focus:z-10 sm:text-sm"
                    :class="[
                      $v.lastName.$error
                        ? 'border-red-600 focus:border-red-600'
                        : 'border-skin-primary focus:border-skin-primary',
                    ]"
                    @blur="$v.lastName.$touch()"
                  />
                  <span
                    v-if="$v.lastName.$error"
                    class="pt-1 text-sm text-red-600 focus:ring-brand-primary pt-[10px] border-skin-primary"
                  >
                    {{ $v.lastName.$errors[0].$message }}
                  </span>
                </div>
                <div class="col-span-12 md:col-span-6">
                  <!--<label for="email-address">{{ $t('form.email') }} *</label>-->
                  <input
                    id="email-address"
                    v-model="state.email"
                    name="email"
                    type="email"
                    :placeholder="$t('form.email') + ' *'"
                    autocomplete="email"
                    :class="[
                      $v.email.$error
                        ? 'border-red-600 focus:border-red-600'
                        : 'border-skin-primary focus:border-skin-primary',
                    ]"
                    class="appearance-none relative block w-full text-[14px] h-11 py-2 px-5 border pt-[10px] placeholder-skin-secondary text-gray-900 rounded-[3px] focus:outline-none focus:ring-gray-300 focus:z-10 sm:text-sm"
                    @blur="$v.email.$touch()"
                  />
                  <span
                    v-if="$v.email.$error"
                    class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
                  >
                    {{ $v.email.$errors[0].$message }}
                  </span>
                </div>
                <div class="col-span-12 md:col-span-6">
                  <!--<label for="phone">{{ $t('form.phone') }} *</label>-->
                  <input
                    id="phone"
                    v-model="state.phone"
                    name="phone"
                    type="text"
                    :placeholder="$t('form.phone') + ' *'"
                    autocomplete="phone"
                    class="appearance-none relative block w-full h-11 text-[14px] py-2 px-5 border pt-[10px] placeholder-skin-secondary text-gray-900 rounded-[3px] focus:outline-none focus:ring-gray-300 focus:z-10 sm:text-sm"
                    :class="[
                      $v.phone.$error
                        ? 'border-red-600 focus:border-red-600'
                        : 'border-skin-primary focus:border-skin-primary',
                    ]"
                    @blur="$v.phone.$touch()"
                  />
                  <span
                    v-if="$v.phone.$error"
                    class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
                  >
                    {{ $v.phone.$errors[0].$message }}
                  </span>
                </div>

                <div class="col-span-12">
                  <!--<label for="comment">{{ $t('form.comment') }} *</label>-->
                  <textarea
                    id="comment"
                    v-model="state.comment"
                    name="comment"
                    type="text"
                    :placeholder="$t('form.comment') + ' *'"
                    autocomplete="comment"
                    class="appearance-none relative block w-full py-2 text-[14px] px-5 pt-[10px] border-skin-primary focus:border-skin-primary border placeholder-skin-secondary text-gray-900 rounded-[3px] focus:outline-none focus:ring-gray-300 focus:z-10 sm:text-sm"
                    rows="5"
                  />
                </div>
                <div class="col-span-12 flex gap-3">
                  <input
                    id="support"
                    v-model="state.support"
                    name="privacy"
                    type="checkbox"
                    class="focus:ring-[#009639] w-6 h-6 mt-[-6px] text-[14px] border text-skin-theme rounded-sm"
                  />
                  <div>
                    <label class="text-[14px]" for="support"
                      >{{ $t("form.consultation") }}
                    </label>
                  </div>
                </div>
                <div class="col-span-12 mt-5">
                  <label class="mb-5 block text-[14px]"
                    >{{ $t("form.privacy") }} *</label
                  >
                  <div class="flex gap-3 items-center">
                    <input
                      id="privacy"
                      v-model="state.checkbox"
                      name="privacy"
                      type="checkbox"
                      class="focus:ring-[#009639] w-6 h-6 mt-[-6px] text-[14px] border text-skin-theme rounded-sm"
                      :class="[
                        $v.checkbox.$error
                          ? 'border-red-600'
                          : 'border-skin-primary',
                      ]"
                    />
                    <div>
                      <label
                        class="text-[14px]"
                        :class="[$v.checkbox.$error ? 'text-red-600' : '']"
                        for="privacy"
                        v-html="$t('checkout.termsAdnConditionsLabel')"
                      >
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="mt-10 pt-10 border-t border-skin-primary mx-0 md:ml-5 md:mr-5"
              >
                <p class="text-[14px] text-skin-secondary leading-5">
                  {{ $t("form.formMandatoryContent") }}
                </p>
              </div>
              <div
                class="flex text-center gap-5 flex-col md:flex-row lg:flex-col justify-center laptop:justify-end laptop:flex-row mobile:prose-a:w-full mobile:prose-a:p-2.5"
              >
                <div class="flex justify-center gap-x-5">
                  <button
                    @click="$emit('close')"
                    class="btn-outline w-full text-center text-primary mt-10 relative group overflow-hidden"
                  >
                    {{ $t("product.configurator.cancel") }}
                  </button>
                  <button
                    class="w-full text-center text-primary btn mt-10 relative group overflow-hidden"
                    type="submit"
                  >
                    {{ $t("form.submit") }}
                  </button>
                </div>
              </div>
            </template>
            <template v-else>
              <p class="py-10 !text-lg font-bold text-center">
                {{ $t("product.quotation.success") }}
              </p>
            </template>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
